import axios, {AxiosError} from "axios";
import EventService from "./event-service";
import Cookies from 'js-cookie';

// Define interfaces for API responses
interface TokenResponse {
    accessToken: string;
    msg?: string;
}

interface ErrorResponse {
    msg: string;
}

export default class ApiService {
    private static instance: ApiService | null = null;

    // In-memory storage for access token (not vulnerable to XSS like localStorage)
    private accessToken: string | null = null;
    private csrfToken: string | null = null;

    // Flag to prevent multiple token refresh requests
    private isRefreshing = false;
    // Queue of requests to retry after token refresh
    private requestsQueue: Array<() => void> = [];

    // Default to '/api' if no explicit API base URI is provided
    private baseUrl = process.env.REACT_APP_API_URI || '/api';


    protected constructor() {
        // Protected constructor verhindert direkte Instanziierung
    }

    public static getInstance(): ApiService {
        if (!ApiService.instance) {
            ApiService.instance = new ApiService();
        }
        return ApiService.instance;
    }


    // Set tokens after login or token refresh
    public setTokens(accessToken: string, csrfToken?: string) {
        this.accessToken = accessToken;
        if (csrfToken) {
            this.csrfToken = csrfToken;
        }
    }

    // Clear tokens on logout
    public clearTokens() {
        this.accessToken = null;
        this.csrfToken = null;
    }

    // Check if user is authenticated
    public isAuthenticated(): boolean {
        return !!this.accessToken
    }

    // Initialize authentication state from cookies
     async initAuth(): Promise<boolean> {
        // If we already have an access token, we're authenticated
        if (this.accessToken) {
            return true;
        }

        // Try to refresh the token using the refresh token cookie
        try {
            return await this.refreshToken();
        } catch (error) {
            console.error('Failed to initialize authentication:', error);
            return false;
        }
    }

    private getUrl(query: string, url?: string): string {
        const reqUrl = url || this.baseUrl;
        if (reqUrl) {
            return reqUrl + query;
        }
        return "";
    }

    // Get headers including Authorization and CSRF token
    private getHeaders(options?: unknown): any {
        const headers: any = {};

        if (this.accessToken) {
            headers['Authorization'] = `Bearer ${this.accessToken}`;
        } else {
            //console.error('No access token found. Please login.');
        }

        // Add CSRF token for non-GET requests
        if (this.csrfToken && options !== 'GET') {
            headers['X-CSRF-Token'] = this.csrfToken;
        }

        return headers;
    }

    // Refresh token when access token expires
    private async refreshToken(): Promise<boolean> {
        if (this.isRefreshing) {
            // Return a promise that resolves when the refresh is complete
            return new Promise<boolean>((resolve) => {
                this.requestsQueue.push(() => resolve(this.isAuthenticated()));
            });
        }
        const csrfToken = Cookies.get('csrfToken');
        if (this.csrfToken || csrfToken) {
            this.isRefreshing = true;

            try {
                const reqUrl = this.getUrl('/refresh-token');
                // Create headers object with conditional CSRF token
                const headers: Record<string, string> = {};
                if (this.csrfToken) {
                    headers['X-CSRF-Token'] = this.csrfToken;
                } else if (csrfToken) {
                    headers['X-CSRF-Token'] = csrfToken;
                }

                // const response = await axios.post(reqUrl, {}, {
                //     withCredentials: true, // Include cookies
                //     headers
                // });
                const response = await axios.post(reqUrl, {}, {
                    headers: headers,
                    withCredentials: true // Include cookies
                })

                // Type assertion for response data
                const tokenResponse = response.data as TokenResponse & { csrfToken?: string };
                if (tokenResponse && tokenResponse.accessToken) {
                    // Store both tokens
                    this.setTokens(tokenResponse.accessToken, tokenResponse.csrfToken);

                    // Process queued requests
                    this.requestsQueue.forEach(callback => callback());
                    this.requestsQueue = [];

                    this.isRefreshing = false;
                    return true;
                }

                this.isRefreshing = false;
                return false;
            } catch (error) {
                // Avoid dumping full Axios error objects (may include headers). Log message only.
                const msg = (error as any)?.message || String(error);
                console.error('Token refresh failed:', msg);
                // Clear tokens on refresh failure
                this.clearTokens();

                // Process queued requests (they will fail due to no token)
                this.requestsQueue.forEach(callback => callback());
                this.requestsQueue = [];

                this.isRefreshing = false;
                return false;
            }
        }
        console.log("No Tokens to refresh found. Please login.")
        return false;
    }

    // Handle API response
    private async handleResponse(promise: Promise<any>): Promise<any> {
        try {
            const response = await promise;
            return response.data;
        } catch (error) {
            // Type guard to check if error is an AxiosError
            const axiosError = error as AxiosError;

            // Handle token expiration
            if (axiosError.response &&
                axiosError.response.status === 401 &&
                axiosError.response.data) {

                // Cast the response data to our ErrorResponse type
                const errorData = axiosError.response.data as ErrorResponse;

                // Hard-stop cases: user/entity no longer valid -> do NOT refresh to avoid loops
                if (
                    errorData.msg === 'User no longer exists.' ||
                    errorData.msg === 'Invalid token for this user.'
                ) {
                    console.error(errorData.msg + ' Clearing tokens and stopping retry.');
                    this.clearTokens();
                    return axiosError.response.status;
                }

                // Refresh-on-expiration cases only
                if (
                    errorData.msg === 'Token expired. Please refresh your token.' ||
                    errorData.msg === 'Session expired due to inactivity.'
                ) {
                    console.error(errorData.msg + " Need to refresh token.");
                    // Try to refresh the token
                    const refreshed = await this.refreshToken();
                    if (refreshed) {
                        // Create a new promise with the updated token instead of recursively calling handleResponse
                        // This prevents potential infinite loops if token refresh keeps failing
                        const config = (axiosError.config as any);
                        if (config) {
                            // Update the Authorization header with the new token
                            config.headers = this.getHeaders(config.method);
                            // Create a new request with the updated config
                            return this.handleResponse(axios(config));
                        }
                    }
                }
            }

            ErrorHandler.dispatchAPIError(error);
            return axiosError.response ? axiosError.response.status : 500;
        }
    }

    public get(query: string, url?: string): any {
        const reqUrl = this.getUrl(query, url);
        return this.handleResponse(
            axios.get(reqUrl, {
                headers: this.getHeaders('GET'),
                withCredentials: true // Include cookies
            })
        );
    }

    public post(query: string, payload: any, url?: string) {
        const reqUrl = this.getUrl(query, url);
        return this.handleResponse(
            axios.post(reqUrl, payload, {
                headers: this.getHeaders(),
                withCredentials: true // Include cookies
            })
        );
    }

    public patch(query: string, payload: any, url?: string) {
        const reqUrl = this.getUrl(query, url);
        return this.handleResponse(
            axios.patch(reqUrl, payload, {
                headers: this.getHeaders(),
                withCredentials: true // Include cookies
            })
        );
    }

    public delete(query: string, url?: string) {
        const reqUrl = this.getUrl(query, url);
        return this.handleResponse(
            axios.delete(reqUrl, {
                headers: this.getHeaders(),
                withCredentials: true // Include cookies
            })
        );
    }
}

export class ErrorHandler {
    private static eventBus = new EventService();

    static onAPIError(callback: (error: unknown) => void) {
        this.eventBus.on("ERROR", callback);
    }

    static dispatchAPIError(error: unknown) {
        this.eventBus.dispatch("ERROR", error);
    }

    static reset() {
        this.eventBus.reset();
    }
}
